'use client';

import { useEffect, useRef, useState } from 'react';

import Contacts2 from '@/components/elements/contacts2';
import Icon2 from '@/components/elements/icon/icon2';
import { useToggle } from '@/hooks/use-toggle';
import { CountryCode } from '@/i18n/countries';
import { getScrollRem } from '@/utils/window-utils';

interface SelectProps {
    title: string;
    country?: CountryCode;
    className?: { [key: string]: string };
    textClassName?: string;
    testId?: string;
}

export default function PseudoSelectContacts({ textClassName = 'text-sm', ...props }: SelectProps) {
    const selectRef = useRef<HTMLDivElement>(null);
    const { toggleValue, handleToggle, setToggleValue } = useToggle(false);
    const [topPos, setTopPos] = useState<string>(getScrollRem() >= 2 ? 'top-10' : 'top-14');

    // closes the dropdown when an outside click occurs
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setToggleValue(false);
            }
        };
        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, [setToggleValue]);

    // shifts the dropdown to align with the header
    useEffect(() => {
        const handlePageScroll = () => {
            setTopPos(getScrollRem() >= 2 ? 'top-10' : 'top-14');
        };
        window.addEventListener('scroll', handlePageScroll);
        return () => window.removeEventListener('scroll', handlePageScroll);
    }, []);

    return (
        <div ref={selectRef} className={`relative z-[1] flex text-sm ${props.className} ${textClassName}`}>
            <div
                data-testid={props.testId}
                onClick={handleToggle}
                className={`mx-4 flex w-full cursor-pointer justify-between gap-1.5 ${props.className && props.className.select}`}
            >
                <button type="button" className={`${props.className} ${textClassName}`}>
                    {props.title}
                </button>
                <Icon2
                    name="phi-caret-down-16"
                    iconClass={`h-5 w-5 transition-transform ${toggleValue ? '-scale-y-100' : ''}`}
                />
            </div>
            <ul
                className={`no-scrollbar absolute right-1/2 z-[1] flex max-h-52 w-max translate-x-1/2 flex-col items-start overflow-y-auto rounded-b-md border border-solid border-gray-500 bg-white py-2 ${topPos} ${toggleValue ? '' : 'hidden'}`}
            >
                <Contacts2
                    size={5}
                    country={props.country}
                    className="h-9 w-full cursor-pointer px-5 py-2 transition-colors duration-100 hover:bg-accent hover:bg-opacity-20"
                />
            </ul>
        </div>
    );
}
